import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { GridActionsCellItem, type GridColDef, type GridRowsProp } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';

import NvrDelete from './NvrDelete.js';
import NvrForm from './NvrForm.js';
import SyncChip from './SyncChip.js';
import { CamerasApiKeys } from '../../api/cameras.api.js';
import { NvrsApiKeys, type NvrSyncStatus, useNvrs, useNvrSyncStatuses, useTriggerNvr } from '../../api/nvrs.api.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Nvr } from '../../models/index.js';
import Table from '../Table/Table.js';
import tableActions from '../Table/tableActions.js';

const VISIBLE_FIELDS = ['name', 'ipAddress', 'port', 'cameras', 'sync', 'actions'];

const NvrsTable = () => {
  const {data = [], isLoading, isError, refetch} = useNvrs();
  const nvrIds = useMemo(() => data.map((n) => n.id), [data]);
  const {data: syncStatuses = {}, refetch: refetchSync} = useNvrSyncStatuses(nvrIds);
  const {
    open: formOpen,
    selectedData: formData,
    onOpen: onFormOpen,
    reset: resetForm,
    setOpen: setFormOpen,
  } = useDialogControls<Nvr>(data);
  const {
    open: deleteOpen,
    selectedData: deleteData,
    onOpen: onDelete,
    reset: resetDelete,
  } = useDialogControls<Nvr>(data);
  const triggerMutation = useTriggerNvr({onSuccess: () => refetchSync()});

  const queryClient = useQueryClient();
  const prevStatuses = useRef<Record<string, NvrSyncStatus['status']>>({});
  useEffect(() => {
    const justSucceeded = Object.entries(syncStatuses).some(([id, s]) => {
      const prev = prevStatuses.current[id];
      return s.status === 'success' && prev && prev !== 'success';
    });
    prevStatuses.current = Object.fromEntries(
      Object.entries(syncStatuses).map(([id, s]) => [id, s.status]),
    );
    if (justSucceeded) {
      queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERAS]);
      queryClient.invalidateQueries([CamerasApiKeys.GET_CAMERA_FOR_PATIENT]);
      queryClient.invalidateQueries([NvrsApiKeys.GET_NVRS]);
      queryClient.invalidateQueries([NvrsApiKeys.GET_NVR]);
    }
  }, [syncStatuses, queryClient]);

  const {canManageCamera, canDeleteCamera} = useCheckPermissions();

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {field: 'name', headerName: 'Name', flex: 1},
      {field: 'ipAddress', headerName: 'IP Address', flex: 1},
      {field: 'port', headerName: 'Port', flex: 1},
      {field: 'cameras', headerName: 'Cameras', flex: 1},
      {
        field: 'sync',
        headerName: 'Sync',
        width: 130,
        sortable: false,
        renderCell: (params) => <SyncChip status={syncStatuses[params.id as string]}/>,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        type: 'actions',
        getActions: (params) => {
          const base = tableActions(onDelete(params.id), onFormOpen(params.id))({
            canEdit: canManageCamera,
            canDelete: canDeleteCamera,
          });
          // Add trigger button
          return [
            ...base,
            (<GridActionsCellItem
              id="trigger"
              label="Rescan"
              onClick={() => triggerMutation.mutate(params.id as string)}
              icon={(
                <Tooltip arrow title={'Rescan NVR for cameras'}>
                    <RefreshIcon fontSize="small"/>
                </Tooltip>
              )}
            />),
          ];
        },
      },
    ];
    return cols.filter((c) => VISIBLE_FIELDS.includes(c.field));
  }, [canManageCamera, canDeleteCamera, onFormOpen, onDelete, triggerMutation, syncStatuses]);

  const rows: GridRowsProp = useMemo(() => {
    return data.map((item) => ({
      id: item.id,
      name: item.name,
      ipAddress: item.ipAddress,
      port: `ONVIF: ${item.onvifPort}, RTSP: ${item.rtspPort}`,
      cameras: (item.cameras || []).map((c) => c.name).join(', '),
    }));
  }, [data]);

  if (isError) {
    return <h1>Error loading NVRs</h1>;
  }

  return (
    <Box sx={{width: '100%'}}>
      <Stack direction={'row'} justifyContent={'space-between'} sx={{my: 1}}>
        <h2 style={{margin: 0}}>NVRs</h2>
        {canManageCamera ? (
          <Tooltip arrow color="primary" placement="bottom" title={'Add a new NVR'}>
            <IconButton color="inherit" aria-label="Add a new NVR" onClick={() => setFormOpen(true)}>
              <AddIcon/>
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      <Table columns={columns} rows={rows} NoRowsOverlayText={'No NVRs available.'} isLoading={isLoading} pageSize={10}
             pageSizeOptions={[10, 25, 50, 100]} toolbar/>
      <NvrForm open={formOpen} onClose={resetForm} nvrId={formData?.id} refetch={refetch}/>
      <NvrDelete open={deleteOpen} onClose={resetDelete} nvrId={deleteData?.id} refetch={refetch}/>
    </Box>
  );
};

export default NvrsTable;
